import {
    CLEAR_STATE,
    DELETE_LEG,
    ERASE_LEGS_SNAPSHOT,
    FETCH_ACTIVITY_DATA_FAIL,
    FETCH_ACTIVITY_DATA_START,
    FETCH_ACTIVITY_DATA_SUCCESS,
    FETCH_ACTIVITY_DATA_SUCCESS_V1,
    FETCH_ALL_CHILDS_METRICS_DATA_FAIL,
    FETCH_ALL_CHILDS_METRICS_DATA_START,
    FETCH_ALL_CHILDS_METRICS_DATA_SUCCESS,
    FETCH_CHILD_ACTIVITY_DATA_FAIL,
    FETCH_CHILD_ACTIVITY_DATA_START,
    FETCH_CHILD_ACTIVITY_DATA_SUCCESS,
    HIDE_ALERT,
    INSERT_LEG,
    MAKE_LEGS_SNAPSHOT,
    MAKE_TRIM_SNAPSHOT,
    PUT_RECONSTRUCTED_ACTIVITY_DATA_FAIL,
    PUT_RECONSTRUCTED_ACTIVITY_DATA_START,
    PUT_RECONSTRUCTED_ACTIVITY_DATA_SUCCESS,
    RESET_TRIM,
    RESTORE_LEGS_SNAPSHOT,
    RESTORE_TRIM_SNAPSHOT,
    SET_CURRENT_LEG_INDEX,
    SET_START_TO_TIMESTAMP,
    SET_TRIM_END,
    SET_TRIM_START,
    TOGGLE_SELECT_LEG_MODAL,
    UPDATE_ACTIVITY_TYPE
} from 'actions/ActionTypes.js';

import {
    fetchActivityDataSuccessV1,
    deleteLeg,
    fetchActivityDataFail,
    fetchActivityDataSuccess,
    fetchAllChildsMetricsDataSuccess,
    fetchChildActivityDataSuccess,
    insertLeg,
    MERGE_STRATEGY_WITH_NEXT_LEG,
    setCurrentLegIndex,
    setStartToTimestamp,
    setTrimEnd,
    setTrimStart,
    makeLegsSnapshot,
    updateActivityType
} from '../../actions/multiSportEditActions';
import { selectLegCount } from './multiSportSelectors';
import {
    binarySearch,
    compare,
    duplicateLastItemOfEveryMember,
    insertItemAt,
    onesComplementIfNegative,
    removeItemAt,
    replaceItemAt,
    toObject
} from './utils/genericUtils';
import { aggregateMetrics, findInDirectTimestamp, findInPolyline } from './utils/multiSportUtils';
import { createTrimIndexData } from './utils/trimIndexData';

/** @typedef {import('./types/entity/LegMetrics').LegMetrics} LegMetrics */
/** @typedef {import('./types/dto/GeoPointDTO').GeoPointDTO} GeoPointDTO */
/** @typedef {import('./types/entity/LegIndexData').LegIndexData} LegIndexData */
/** @typedef {import('./types/entity/MetricsByKey').MetricsByKey} MetricsByKey */
/** @typedef {import('./types/dto/ActivityDataDTO').ActivityDataDTO} ActivityDataDTO */
/** @typedef {import('./types/entity/MultiSportState').MultiSportState} MultiSportState */
/** @typedef {import('./types/entity/MultiSportState').TrimEventType} TrimEventType */
/** @typedef {import('../../actions/multiSportEditActions').MergeStrategy} MergeStrategy */
/** @typedef {import('./types/dto/ActivityMetricsDTO').ActivityMetricsDTO} ActivityMetricsDTO */
/** @typedef {import('./types/dto/ActivityMetricsDTO_v2').ActivityMetricsDTO_v2} ActivityMetricsDTO_v2 */
/** @typedef {import('./types/entity/TrimIndexData').TrimIndexData} TrimIndexData */

/** @type {MultiSportState} */
const INITIAL_STATE = {
    attributes: {
        hasInvalidStartEndTime: false,
        hasParentActivityData: false,
        hasChildActivityData: false,
        hasMetricsData: false,
        hasError: false,
        activityData: null,
        metricsByKey: null,
        errorStatus: null
    },
    putReconstructedActivityAttributes: {
        isSubmitting: false,
        hasError: false,
        showAlert: false
    },
    currentLegIndex: 0 /** defaults to first leg */,
    activityIds: [] /** activity ids of the legs */,
    legDataByActivityId: null,
    // Metrics data for the whole activity (childs metrics concatenated)
    completeMetricsByMetricsDescriptor: null,
    legIndexData: null,
    initialLegIndexData: [],
    legsSnapshotByName: {},
    trim: {
        start: null,
        end: null,
        eventType: null
    },
    trimSnapshot: {
        start: null,
        end: null
    }
};

let NEW_LEG_ACTIVITY_ID = -1;

/************************************************************************************************************************
 * Utility functions
 ************************************************************************************************************************/

/**
 * @param {number} legIndex
 * @param {number} legCount
 */
export const checkLegIndexBoundsForRemove = (legIndex, legCount) => {
    if (legIndex < 0) {
        throw new Error('Negative legIndex.');
    }
    if (legIndex >= legCount) {
        throw new Error(`LegIndex must be < legCount (${legCount})`);
    }
    if (legCount === 1) {
        throw new Error('Just one leg left, cannot remove.');
    }
};

/**
 * e.g. for 2 legs
 * legIndexData.length = 3
 *       current: 5-25
 *       |
 *       v
 * [0,   5,   25]
 *  0th  1st     <- legIndex
 *
 * @param {LegIndexData[]} legIndexData
 * @param {number} legIndex
 */
const removeByMergingWithPrevious = (legIndexData, legIndex) => {
    checkLegIndexBoundsForRemove(legIndex, legIndexData.length - 1);
    let newLegIndexData = legIndexData;
    newLegIndexData = removeItemAt(newLegIndexData, legIndex);
    if (legIndex === 0) {
        newLegIndexData = replaceItemAt(newLegIndexData, 0, {
            ...newLegIndexData[0],
            polylineStartIdx: 0,
            metricsStartIdx: 0,
            timestamp: null
        });
    }
    return newLegIndexData;
};

/**
 * e.g. for 3 legs
 * legIndexData.length = 4
 *           current: 10-25
 *            |
 *            v
 * [0,   5,   10,   25]
 *  0th  1st  2nd   <- legIndex
 *
 * @param {LegIndexData[]} legIndexData
 * @param {number} legIndex the leg to remove
 */
const removeByMergingWithNext = (legIndexData, legIndex) => {
    const numLegs = legIndexData.length - 1;
    checkLegIndexBoundsForRemove(legIndex, numLegs);
    const deleted = legIndexData[legIndex];
    let newLegIndexData = legIndexData;

    // set the indexes of the next leg as if it got merged with the deleted
    newLegIndexData = replaceItemAt(newLegIndexData, legIndex + 1, {
        ...newLegIndexData[legIndex + 1],
        metricsStartIdx: deleted.metricsStartIdx,
        polylineStartIdx: deleted.polylineStartIdx,
        timestamp: deleted.timestamp
    });
    // remove
    newLegIndexData = removeItemAt(newLegIndexData, legIndex);

    return newLegIndexData;
};

/**
 * @param {{
 *  startTimestamp: number; // based on this will all the other indexes be determined
 *  directTimestamp: number[];
 *  polyline: GeoPointDTO[];
 *  activityId: number;
 *  typeKey: string;
 *  typeId: number;
 * }} param0
 */
const createLegIndexDatum = ({ startTimestamp, directTimestamp, polyline, activityId, typeKey, typeId }) => {
    const { index: metricsStartIdx, isExactMatch } = findInDirectTimestamp(directTimestamp, startTimestamp);
    const { index: polylineStartIdx } = findInPolyline(polyline, startTimestamp);

    /** @type LegIndexData */
    const legIndexData = {
        activityId,
        typeKey,
        typeId,
        metricsStartIdx,
        polylineStartIdx,
        timestamp: isExactMatch ? null : startTimestamp
    };
    return legIndexData;
};

/**
 * @param {number} deletedLegIndex
 * @param {number} currentLegIndex
 * @param {MergeStrategy} mergeStrategy
 */
export const calculateCurrentLegIndex = (deletedLegIndex, currentLegIndex, mergeStrategy) => {
    const newCurrentLegIndex = currentLegIndex >= deletedLegIndex ? Math.max(currentLegIndex - 1, 0) : currentLegIndex;
    return newCurrentLegIndex;
};

const mandatoryMetricKeys = [
    'sumDistance',
    'sumDuration',
    'directSpeed',
    'directPower',
    'directElevation',
    'directHeartRate',
    'directTimestamp'
];

/**
 * @param {ActivityMetricsDTO} activityMetricsDTO
 * @returns {MetricsByKey}
 */
const createMetricsByKey = (activityMetricsDTO) => {
    const metricsWithoutPolyline = aggregateMetrics(activityMetricsDTO);
    const augmentedMetrics = mandatoryMetricKeys
        .filter((key) => !(key in metricsWithoutPolyline))
        .map(/** @returns {[string, number[]]} */ (key) => [key, []])
        .reduce(toObject, {});
    const metricsByKey = /** @type {MetricsByKey} */ ({
        ...metricsWithoutPolyline,
        ...augmentedMetrics,
        polyline:
            activityMetricsDTO.geoPolylineDTO && activityMetricsDTO.geoPolylineDTO.polyline
                ? activityMetricsDTO.geoPolylineDTO.polyline
                : []
    });
    // duplicate last item for "imaginary" leg
    duplicateLastItemOfEveryMember(metricsByKey);
    return metricsByKey;
};

/**
 * @param {ActivityMetricsDTO_v2} activityMetricsDTO
 * @param {MetricsByKey} metricsByKey
 */
export const createLegIndexData = (activityMetricsDTO, metricsByKey) => {
    /** @type {LegIndexData[]} */
    const legIndexData = activityMetricsDTO.activityReconstructDTO.reconstructRequest.map(
        ({ startTime, activityId, activityTypeDTO }) => ({
            activityId,
            typeKey: activityTypeDTO.typeKey,
            typeId: activityTypeDTO.typeId,
            metricsStartIdx: metricsByKey.directTimestamp.indexOf(startTime),
            polylineStartIdx: onesComplementIfNegative(
                binarySearch(metricsByKey.polyline, startTime, (a, b) => compare(a, b.time))
            ),
            timestamp: null
        })
    );
    // add last item, marking the start of the "imaginary" leg
    // as the last item is duplication of before the last item, "imaginary" leg can start at exactly the last item
    legIndexData.push({
        activityId: null,
        typeKey: null,
        typeId: null,
        metricsStartIdx: metricsByKey.directTimestamp.length - 1,
        polylineStartIdx: metricsByKey.polyline.length - 1,
        timestamp: null
    });

    return legIndexData;
};

/**
 * @param {ActivityMetricsDTO_v2} activityMetricsDTO
 * @param {MetricsByKey} completeMetrics
 * @returns boolean
 */
const checkForValidStartEndTime = (activityMetricsDTO, completeMetrics) => {
    // checks if main reconstruct request has missing start or end timestamps
    const hasInvalidStartEndTime = activityMetricsDTO.activityReconstructDTO.reconstructRequest.some(
        ({ startTime, endTime }) => !startTime || !endTime
    );

    // if all start and end times exist, checks if the start/end times are out of bounds compared
    // to the metrics (directTimestamp) data provided by API
    let hasOutOfBoundsStartEndTime = false;
    if (!hasInvalidStartEndTime) {
        const legsRequest = activityMetricsDTO.activityReconstructDTO.reconstructRequest,
            firstLegStartTime = legsRequest.reduce((prev, curr) => (prev.startTime < curr.startTime ? prev : curr))
                .startTime,
            lastLegEndtime = legsRequest.reduce((prev, curr) => (prev.endTime > curr.endTime ? prev : curr)).endTime;

        const directTimestamps = completeMetrics.directTimestamp,
            firstTimestamp = directTimestamps.reduce((prev, curr) => (prev < curr ? prev : curr)),
            lastTimestamp = directTimestamps.reduce((prev, curr) => (prev > curr ? prev : curr));

        hasOutOfBoundsStartEndTime = !(
            firstLegStartTime >= firstTimestamp &&
            firstLegStartTime <= lastTimestamp &&
            lastLegEndtime >= firstTimestamp &&
            lastLegEndtime <= lastTimestamp
        );
    }

    return hasInvalidStartEndTime || hasOutOfBoundsStartEndTime;
};

/**
 * @param {MetricsByKey} completeMetrics
 * @returns {{ start: TrimIndexData; end: TrimIndexData; eventType: TrimEventType}}
 */
export const createTrim = (completeMetrics) => {
    /** @type {TrimIndexData} */
    const start = {
        timestamp: null,
        metricsStartIdx: 0,
        polylineStartIdx: 0
    };
    // initial trim end should point to before the last item; last item of completeMetrics is a duplicate
    /** @type {TrimIndexData} */
    const end = {
        timestamp: null,
        metricsStartIdx: completeMetrics.directTimestamp.length - 2,
        polylineStartIdx: completeMetrics.polyline.length - 2
    };
    return {
        start,
        end,
        eventType: 'ON_CHANGE'
    };
};

/************************************************************************************************************************
 * Action handlers
 ************************************************************************************************************************/

/**
 * @param {MultiSportState} state
 */
const fetchActivityDataStartActionHandler = (state) => {
    return {
        ...state,
        attributes: {
            ...state.attributes,
            hasError: false
        }
    };
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof fetchActivityDataSuccess>} action
 * @returns {MultiSportState}
 */
const fetchActivityDataSuccessActionHandler = (state, action) => {
    const { activityData } = action.payload;
    return {
        ...state,
        attributes: {
            ...state.attributes,
            hasError: false,
            hasParentActivityData: true,
            activityData
        }
    };
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof fetchActivityDataFail>} action
 *
 */
const fetchActivityDataFailActionHandler = (state, action) => {
    const { response } = action.payload;
    return {
        ...state,
        attributes: {
            ...state.attributes,
            hasError: true,
            errorStatus: response.status
        }
    };
};

/**
 * @param {MultiSportState} state
 */
const fetchChildActivityDataStartActionHandler = (state) => {
    return {
        ...state,
        attributes: {
            ...state.attributes,
            hasChildActivityData: false
        }
    };
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof fetchChildActivityDataSuccess>} action
 * @returns {MultiSportState}
 */
const fetchChildActivityDataSuccessActionHandler = (state, action) => {
    const { childActivities } = action.payload;

    const activityIds = childActivities.map(({ activityId }) => activityId);
    const legDataByActivityId = childActivities
        .map((activity) => /** @type {[string, ActivityDataDTO]} */ ([activity.activityId.toString(), activity]))
        .reduce(toObject, /** @type {{[key: number]: ActivityDataDTO}} */ ({}));

    return {
        ...state,
        attributes: {
            ...state.attributes,
            hasChildActivityData: true
        },
        activityIds,
        legDataByActivityId
    };
};

const fetchChildActivityDataFail = (state) => {
    // @todo what should be returned here?
    return {
        ...state
    };
};

const fetchAllChildsMetricsDataStart = (state, action) => {
    const { payload } = action;
    const { activityId } = payload;

    return {
        ...state,
        legMetricsByActivityId: {
            ...state.legMetricsByActivityId,
            [activityId]: {
                isFetching: true
            }
        }
    };
};

/**
 * Fetches one big chunk of metrics, containing a continuous data for all legs
 *
 * @param {MultiSportState} state
 * @param {ReturnType<typeof fetchAllChildsMetricsDataSuccess>} action
 * @returns {MultiSportState}
 */
const fetchAllChildsMetricsDataSuccessActionHandler = (state, action) => {
    const { activityMetricsDTO } = action.payload;
    const metricsByKey = createMetricsByKey(activityMetricsDTO);
    const legIndexData = createLegIndexData(activityMetricsDTO, metricsByKey);
    const trim = createTrim(metricsByKey);
    const hasInvalidStartEndTime = checkForValidStartEndTime(activityMetricsDTO, metricsByKey);

    return {
        ...state,
        attributes: {
            ...state.attributes,
            hasMetricsData: true,
            hasInvalidStartEndTime
        },
        completeMetricsByMetricsDescriptor: metricsByKey,
        legIndexData,
        initialLegIndexData: [...legIndexData],
        trim,
        trimSnapshot: { ...trim }
    };
};

const fetchAllChildsMetricsDataFail = (state, action) => {
    const { data } = action;
    const { activityId } = data;

    return {
        ...state,
        legMetricsByActivityId: {
            ...state.legMetricsByActivityId,
            [activityId]: {
                hasError: true // @TODO resolve error handling
            }
        }
    };
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof fetchActivityDataSuccessV1>} action
 * @returns {MultiSportState}
 */
const fetchActivityDataSuccessV1ActionHandler = (state, action) => {
    const { metricsByKey, legIndexData } = action.payload;
    const initialLegIndexData = [...legIndexData];
    const trim = createTrim(metricsByKey);

    /** @type {MultiSportState} */
    const newState = {
        ...state,
        attributes: {
            ...state.attributes,
            hasMetricsData: true
        },
        completeMetricsByMetricsDescriptor: metricsByKey,
        legIndexData,
        initialLegIndexData,
        trim,
        trimSnapshot: { ...trim }
    };
    return newState;
};

const toggleSelectLegModalActionHandler = (state, action) => {
    return {
        ...state,
        showSelectLegModal: action.payload
    };
};

const clearStateActionHandler = () => {
    return INITIAL_STATE;
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof insertLeg>} action
 * @returns {MultiSportState}
 */
const insertLegActionHandler = (state, action) => {
    const { timestamp, typeKey, typeId, insertAtIndex } = action.payload;
    const { legIndexData, completeMetricsByMetricsDescriptor } = state;
    const { directTimestamp, polyline } = completeMetricsByMetricsDescriptor;

    const legIndexDatum = createLegIndexDatum({
        startTimestamp: timestamp,
        directTimestamp,
        polyline,
        activityId: NEW_LEG_ACTIVITY_ID--, // each newly added leg should have a unique index, for the case when the activity type is changed
        typeKey,
        typeId
    });
    const newLegIndexData = insertItemAt(legIndexData, insertAtIndex, legIndexDatum);

    return {
        ...state,
        legIndexData: newLegIndexData
    };
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof deleteLeg>} action
 * @returns {MultiSportState}
 */
export const deleteLegActionHandler = (state, action) => {
    const { legIndex, mergeStrategy = MERGE_STRATEGY_WITH_NEXT_LEG } = action.payload;
    const { legIndexData, currentLegIndex } = state;

    const isFirstLeg = legIndex === 0;
    const actualMergeStrategy = isFirstLeg ? MERGE_STRATEGY_WITH_NEXT_LEG : mergeStrategy;

    const newLegIndexData =
        actualMergeStrategy === MERGE_STRATEGY_WITH_NEXT_LEG
            ? removeByMergingWithNext(legIndexData, legIndex)
            : removeByMergingWithPrevious(legIndexData, legIndex);
    const newCurrentLegIndex = calculateCurrentLegIndex(legIndex, currentLegIndex, mergeStrategy);

    return {
        ...state,
        currentLegIndex: newCurrentLegIndex,
        legIndexData: newLegIndexData
    };
};

/**
 * sets the index of the current leg (the selected leg in other words)
 * @param {MultiSportState} state
 * @param {ReturnType<typeof setCurrentLegIndex>} action
 * @returns {MultiSportState}
 */
const setCurrentLegIndexActionHandler = (state, action) => {
    let { currentLegIndex } = action.payload;
    const maxLegIndex = Math.max(selectLegCount(state) - 1, 0);
    if (currentLegIndex < 0) {
        currentLegIndex = 0;
    }
    if (currentLegIndex > maxLegIndex) {
        currentLegIndex = maxLegIndex;
    }
    return {
        ...state,
        currentLegIndex
    };
};

/**
 * sets both metrics and polyline start
 * @param {MultiSportState} state
 * @param {ReturnType<typeof setStartToTimestamp>} action
 * @returns {MultiSportState}
 */
const setStartToTimestampActionHandler = (state, action) => {
    const { legIndex, timestamp } = action.payload;
    const { completeMetricsByMetricsDescriptor, legIndexData } = state;
    const { directTimestamp, polyline } = completeMetricsByMetricsDescriptor;
    const { activityId, typeKey, typeId } = legIndexData[legIndex];
    const legIndexDatum = createLegIndexDatum({
        startTimestamp: timestamp,
        directTimestamp,
        polyline,
        activityId,
        typeKey,
        typeId
    });
    const newLegIndexData = replaceItemAt(legIndexData, legIndex, legIndexDatum);

    /** @type {MultiSportState} */
    const newState = {
        ...state,
        legIndexData: newLegIndexData
    };
    return newState;
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof makeLegsSnapshot>} action
 * @returns {MultiSportState}
 */
const makeLegsSnapshotActionHandler = (state, action) => {
    const { snapshotName } = action.payload;
    const snapshot = [...state.legIndexData];
    return {
        ...state,
        legsSnapshotByName: {
            ...state.legsSnapshotByName,
            [snapshotName]: snapshot
        }
    };
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof makeLegsSnapshot>} action
 * @returns {MultiSportState}
 */
const restoreLegsSnapshotActionHandler = (state, action) => {
    const { snapshotName } = action.payload;
    const snapshot = state.legsSnapshotByName[snapshotName];

    return {
        ...state,
        ...(snapshot && { legIndexData: [...snapshot] })
    };
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof makeLegsSnapshot>} action
 * @returns {MultiSportState}
 */
const eraseLegsSnapshotActionHandler = (state, action) => {
    const { snapshotName } = action.payload;
    const { [snapshotName]: deletedKey, ...legsSnapshotByName } = state.legsSnapshotByName;
    return {
        ...state,
        legsSnapshotByName
    };
};

/** @param {MultiSportState} state */
const putReconstructedActivityDataStartActionHandler = (state) => ({
    ...state,
    putReconstructedActivityAttributes: {
        ...state.putReconstructedActivityAttributes,
        isSubmitting: true
    }
});

/** @param {MultiSportState} state */
const putReconstructedActivityDataSuccessActionHandler = (state) => ({
    ...state,
    putReconstructedActivityAttributes: {
        ...state.putReconstructedActivityAttributes,
        isSubmitting: false,
        hasError: false
    }
});

/**
 * @param {MultiSportState} state
 * @returns {MultiSportState}
 */
const putReconstructedActivityDataFailActionHandler = (state) => ({
    ...state,
    putReconstructedActivityAttributes: {
        ...state.putReconstructedActivityAttributes,
        isSubmitting: false,
        hasError: true,
        showAlert: true
    }
});

/**
 * @param {MultiSportState} state
 * @returns {MultiSportState}
 */
const hideAlertActionHandler = (state) => ({
    ...state,
    putReconstructedActivityAttributes: {
        ...state.putReconstructedActivityAttributes,
        showAlert: false
    }
});

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof setTrimStart>} action
 * @returns {MultiSportState}
 */
const setTrimStartActionHandler = (state, action) => {
    const { timestamp, eventType } = action.payload;
    const { directTimestamp, polyline } = state.completeMetricsByMetricsDescriptor;

    const trim = {
        ...state.trim,
        start: createTrimIndexData({ timestamp, directTimestamp, polyline }),
        eventType
    };

    return {
        ...state,
        trim
    };
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<typeof setTrimEnd>} action
 * @returns {MultiSportState}
 */
const setTrimEndActionHandler = (state, action) => {
    const { timestamp, eventType } = action.payload;
    const { directTimestamp, polyline } = state.completeMetricsByMetricsDescriptor;
    const end = createTrimIndexData({ timestamp, directTimestamp, polyline });

    return {
        ...state,
        trim: {
            ...state.trim,
            end,
            eventType
        }
    };
};

/**
 * @param {MultiSportState} state
 * @returns {MultiSportState}
 */
const makeTrimSnapshotActionHandler = (state) => ({
    ...state,
    trimSnapshot: {
        start: state.trim.start,
        end: state.trim.end
    }
});

/**
 * @param {MultiSportState} state
 * @returns {MultiSportState}
 */
const restoreTrimSnapshotActionHandler = (state) => ({
    ...state,
    trim: {
        ...state.trimSnapshot,
        eventType: 'SNAPSHOT_RESTORE'
    }
});

/**
 * @param {MultiSportState} state
 * @returns {MultiSportState}
 */
const resetTrimActionHandler = (state) => {
    const trim = createTrim(state.completeMetricsByMetricsDescriptor);
    return {
        ...state,
        trim,
        trimSnapshot: { ...trim }
    };
};

/**
 * @param {MultiSportState} state
 * @param {ReturnType<updateActivityType>} action
 * @returns {MultiSportState}
 */
const updateActivityTypeActionHandler = (state, action) => {
    const { activityId, typeKey, typeId } = action.payload;

    return {
        ...state,
        legIndexData: state.legIndexData.map((leg) => {
            if (leg.activityId !== activityId) return leg;
            return { ...leg, typeKey, typeId };
        })
    };
};

// ======= Handler Assignments =======
const handlers = {
    [FETCH_ACTIVITY_DATA_START]: fetchActivityDataStartActionHandler,
    [FETCH_ACTIVITY_DATA_SUCCESS]: fetchActivityDataSuccessActionHandler,
    [FETCH_ACTIVITY_DATA_FAIL]: fetchActivityDataFailActionHandler,
    [FETCH_CHILD_ACTIVITY_DATA_START]: fetchChildActivityDataStartActionHandler,
    [FETCH_CHILD_ACTIVITY_DATA_SUCCESS]: fetchChildActivityDataSuccessActionHandler,
    [FETCH_CHILD_ACTIVITY_DATA_FAIL]: fetchChildActivityDataFail,
    [FETCH_ALL_CHILDS_METRICS_DATA_START]: fetchAllChildsMetricsDataStart,
    [FETCH_ALL_CHILDS_METRICS_DATA_SUCCESS]: fetchAllChildsMetricsDataSuccessActionHandler,
    [FETCH_ALL_CHILDS_METRICS_DATA_FAIL]: fetchAllChildsMetricsDataFail,
    [FETCH_ACTIVITY_DATA_SUCCESS_V1]: fetchActivityDataSuccessV1ActionHandler,
    [TOGGLE_SELECT_LEG_MODAL]: toggleSelectLegModalActionHandler,
    [SET_CURRENT_LEG_INDEX]: setCurrentLegIndexActionHandler,
    [SET_START_TO_TIMESTAMP]: setStartToTimestampActionHandler,
    [CLEAR_STATE]: clearStateActionHandler,
    [INSERT_LEG]: insertLegActionHandler,
    [DELETE_LEG]: deleteLegActionHandler,
    [MAKE_LEGS_SNAPSHOT]: makeLegsSnapshotActionHandler,
    [RESTORE_LEGS_SNAPSHOT]: restoreLegsSnapshotActionHandler,
    [ERASE_LEGS_SNAPSHOT]: eraseLegsSnapshotActionHandler,
    [PUT_RECONSTRUCTED_ACTIVITY_DATA_START]: putReconstructedActivityDataStartActionHandler,
    [PUT_RECONSTRUCTED_ACTIVITY_DATA_SUCCESS]: putReconstructedActivityDataSuccessActionHandler,
    [PUT_RECONSTRUCTED_ACTIVITY_DATA_FAIL]: putReconstructedActivityDataFailActionHandler,
    [HIDE_ALERT]: hideAlertActionHandler,
    [SET_TRIM_START]: setTrimStartActionHandler,
    [SET_TRIM_END]: setTrimEndActionHandler,
    [MAKE_TRIM_SNAPSHOT]: makeTrimSnapshotActionHandler,
    [RESTORE_TRIM_SNAPSHOT]: restoreTrimSnapshotActionHandler,
    [RESET_TRIM]: resetTrimActionHandler,
    [UPDATE_ACTIVITY_TYPE]: updateActivityTypeActionHandler
};

// ======= Default Action ======
export default function multiSportActivitySummaryReducer(state = INITIAL_STATE, action) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}
